/**
 * TUVISA Calendar - Unified Date Utilities
 * 
 * IMPORTANTE: Este módulo es la ÚNICA fuente de verdad para
 * cálculos de fechas y turnos en todo el frontend.
 * 
 * NO uses new Date(dateString) directamente en otros archivos.
 * Usa siempre estas funciones.
 */

/**
 * Parsea un string YYYY-MM-DD como fecha LOCAL (no UTC)
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @returns {Date} Fecha local a medianoche
 */
export const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr).split('T')[0]; // Remove time part if present
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day); // Creates local midnight
};

/**
 * Obtiene el número de semana ISO de una fecha
 * @param {Date} d - Fecha
 * @returns {number} Número de semana ISO (1-53)
 */
const getWeekNumber = (d) => {
    // Create a copy in UTC using local date components
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // Set to nearest Thursday (ISO week starts Monday)
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    // Get first day of year
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    // Calculate week number
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

/**
 * Normaliza un turno a formato 'M' o 'T'
 * @param {string} turno - Turno en cualquier formato ('M', 'Mañana', 'T', 'Tarde')
 * @returns {string} 'M' o 'T'
 */
export const normalizeTurno = (turno) => {
    if (!turno) return 'M'; // Default to morning
    const t = String(turno).trim().toUpperCase();
    if (t === 'M' || t === 'MAÑANA' || t.startsWith('MAÑ')) return 'M';
    if (t === 'T' || t === 'TARDE') return 'T';
    return t.charAt(0) === 'M' ? 'M' : 'T'; // Fallback
};

/**
 * Calcula el turno que corresponde a una fecha dada basándose en el turno inicial
 * y la alternancia semanal desde el 1 de enero de 2026.
 * 
 * IMPORTANTE: En TUVISA, el turno cambia de SÁBADO a DOMINGO.
 * Por eso sumamos +1 día antes de calcular la semana, para que el domingo
 * se cuente con la semana siguiente (nuevo turno).
 * 
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @param {string} initialTurno - Turno inicial del conductor ('M', 'T', 'Mañana', 'Tarde')
 * @returns {string} 'M' o 'T'
 */
export const getTurnoForDate = (dateStr, initialTurno) => {
    if (!initialTurno) return 'M';

    // Normalize the initial turno
    const normalizedInitial = normalizeTurno(initialTurno);

    // Parse dates as LOCAL
    const target = parseLocalDate(dateStr);
    const ref = new Date(2026, 0, 1); // Jan 1, 2026 local (Thursday)

    if (!target) return normalizedInitial;

    // Add +1 day to both dates so Sunday shifts to next week
    // This makes the turno change happen at Saturday→Sunday boundary
    const targetAdjusted = new Date(target);
    targetAdjusted.setDate(targetAdjusted.getDate() + 1);

    const refAdjusted = new Date(ref);
    refAdjusted.setDate(refAdjusted.getDate() + 1);

    // Calculate week difference using adjusted dates
    const shifts = ['M', 'T'];
    const weekDiff = getWeekNumber(targetAdjusted) - getWeekNumber(refAdjusted);
    const initialIndex = shifts.indexOf(normalizedInitial);
    const newIndex = ((initialIndex + weekDiff) % 2 + 2) % 2;

    return shifts[newIndex];
};

/**
 * Comprueba si una fecha es domingo
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @returns {boolean}
 */
export const isSunday = (dateStr) => {
    const date = parseLocalDate(dateStr);
    return date ? date.getDay() === 0 : false;
};

/**
 * Comprueba si un festivo afecta al turno del conductor
 * (Es decir, si es un día libre para el conductor)
 * 
 * @param {Object} festivo - { turnoAfectado: 'M'|'T'|'ambos' }
 * @param {string} conductorTurnoEnFecha - El turno del conductor ESE día ('M' o 'T')
 * @returns {boolean} true si el conductor tiene el día libre
 */
export const isPartialHolidayForTurno = (festivo, conductorTurnoEnFecha) => {
    if (!festivo) return false;
    const turnoAfectado = festivo.turno_afectado || festivo.turnoAfectado || 'ambos';

    // 'ambos' = día de trabajo con prima (NO es día libre)
    if (turnoAfectado === 'ambos') return false;

    // Partial holiday: solo afecta si el turno del conductor coincide
    return turnoAfectado === conductorTurnoEnFecha;
};

